import type { ReactNode } from 'react';
import type { ExperimentStatus, HoldoutStatus } from '@gonfalon/experiments/types';
import { ExperimentStatusKind, HoldoutStatusKind } from '@gonfalon/experiments/types';
import type { IconProps } from '@launchpad-ui/icons';
import { Icon } from '@launchpad-ui/icons';
import cx from 'clsx';

import styles from './ExperimentStatusIcon.module.css';
/**
 * Helper component wraps an icon and label in an layout element
 *
 * @param param.kind - the display style
 * @param param.icon - the status' icon
 * @param param.label - a descriptive label describing the status
 * @returns
 */
const IconWrapper = ({
  icon,
  label,
  className,
  kind,
}: {
  icon: ReactNode;
  kind: 'minimal' | 'normal' | 'text-only';
  label: string;
  className?: string;
}) => (
  <span aria-label="Experiment status" className={`u-flex u-flex-row u-flex-middle ${className}`}>
    {kind !== 'text-only' && icon}
    {['normal', 'text-only'].includes(kind) && <span className="u-pl-s">{label}</span>}
  </span>
);

/**
 * Component renders an experiment status badge
 *
 * @param param.status - the experiment status to be presented
 * @param param.kind - the display style for the status
 */
export const ExperimentStatusIcon = ({
  status,
  className,
  kind = 'normal',
  isHoldout = false,
}: {
  kind?: 'minimal' | 'normal' | 'text-only';
  status: ExperimentStatus | HoldoutStatus;
  className?: string;
  isHoldout?: boolean;
}) => {
  const iconProps = {
    size: 'small' as IconProps['size'],
  };

  const experimentRunning = status === ExperimentStatusKind.RUNNING;
  const experimentNotStarted = status === ExperimentStatusKind.NOT_STARTED;
  const experimentPausedOrStopped = status === ExperimentStatusKind.STOPPED;

  if (status === HoldoutStatusKind.CREATED) {
    return (
      <IconWrapper
        kind={kind}
        className={className}
        icon={<Icon name="created" className={cx(styles.ExperimentStatusIconNotStarted)} {...iconProps} />}
        label="Created"
      />
    );
  }

  if (status === HoldoutStatusKind.ENABLED) {
    return (
      <IconWrapper
        kind={kind}
        className={className}
        icon={<Icon name="holdout" className={cx(styles.ExperimentStatusIconEnabled)} {...iconProps} />}
        label="Ready"
      />
    );
  }

  if (status === HoldoutStatusKind.RUNNING && isHoldout) {
    return (
      <IconWrapper
        kind={kind}
        className={className}
        icon={<Icon name="flask-bolt" className={cx(styles.ExperimentStatusIconClean)} {...iconProps} />}
        label="Analyzing"
      />
    );
  }

  if (status === HoldoutStatusKind.ENDED) {
    return (
      <IconWrapper
        kind={kind}
        className={className}
        icon={<Icon name="check-circle" className={cx(styles.ExperimentStatusIconStopped)} {...iconProps} />}
        label="Complete"
      />
    );
  }

  if (experimentRunning && !isHoldout) {
    return (
      <IconWrapper
        kind={kind}
        className={className}
        icon={<Icon name="play" className={cx(styles.ExperimentStatusIconRunning)} {...iconProps} />}
        label="Running"
      />
    );
  }

  if (experimentNotStarted) {
    return (
      <IconWrapper
        kind={kind}
        className={className}
        icon={<Icon name="clock-alert" className={cx(styles.ExperimentStatusIconNotStarted)} {...iconProps} />}
        label="Not started"
      />
    );
  }

  if (experimentPausedOrStopped) {
    return (
      <IconWrapper
        kind={kind}
        className={className}
        icon={<Icon name="stop" className={cx(styles.ExperimentStatusIconStopped)} {...iconProps} />}
        label="Stopped"
      />
    );
  }

  return null;
};
